import { useSuspenseQuery } from '@apollo/client/react';
import { ErrorAlert } from '@optools/components/alerts/error';
import { PageTitle } from '@optools/components/layout/typography';
import { DebouncedSearch } from '@optools/components/search/debounced-search';
import { SKYVIEW_CONFIGS } from '@optools/services/operator-gateway/btc/queries';
import { useTeamsQuery } from '@optools/store/statsApiSlice';
import { SkyviewConfigCard } from '@optools/tools/btc-tools/components/skyview/skyview-config-card';
import { SkyviewGlobalConfigActions } from '@optools/tools/btc-tools/components/skyview/skyview-global-config-actions';
import { TeamConfigGrid } from '@optools/tools/btc-tools/components/team-config-card';
import { groupSkyviewConfigsByTeam } from '@optools/tools/btc-tools/utils/skyview';
import { filterTeamsBySearchTerm } from '@optools/utils/filter';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_auth/btc-tools/configs/skyview')({
  component: RouteComponent,
});

export function RouteComponent() {
  const [term, setTerm] = useState('');
  const { data: teams = [] } = useTeamsQuery();

  const { data, error, refetch } = useSuspenseQuery(SKYVIEW_CONFIGS, {
    context: { skipErrorNotification: true },
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    return <ErrorAlert error={error} title="Skyview Configs Error" onRetry={refetch} />;
  }

  const { skyviewConfigs } = data;

  const configsByTeam = groupSkyviewConfigsByTeam(skyviewConfigs, teams);
  const filteredConfigs = filterTeamsBySearchTerm(configsByTeam, term);

  const allConfigsEnabled = skyviewConfigs.every(({ enabled }) => enabled);
  const someConfigsEnabled = skyviewConfigs.some(({ enabled }) => enabled);

  return (
    <div className="space-y-4">
      <PageTitle>Skyview Configs</PageTitle>

      <div className="flex w-full flex-wrap justify-between gap-4">
        <DebouncedSearch placeholder="Search teams" onChange={setTerm} />
        <SkyviewGlobalConfigActions
          enabled={allConfigsEnabled}
          partiallyEnabled={someConfigsEnabled && !allConfigsEnabled}
        />
      </div>

      <TeamConfigGrid>
        {filteredConfigs.map(({ configs, team, teamId }) => (
          <SkyviewConfigCard key={teamId} configs={configs} team={team} teamId={teamId} />
        ))}
      </TeamConfigGrid>
    </div>
  );
}
